import type { CatalogEntry, IndicatorCategoryId } from "./catalog";
import { indicatorCatalog, indicatorCatalogById } from "./catalog";
import { createAdvancedMA } from "@/lib/chart-helpers/indicators-KLine/advancedMA";
import { createBollingerBands } from "@/lib/chart-helpers/indicators-KLine/bollingerBands";
import { createRSI } from "@/lib/chart-helpers/indicators-KLine/rsi";
import { createMACD } from "@/lib/chart-helpers/indicators-KLine/macd";
import { createStochastic } from "@/lib/chart-helpers/indicators-KLine/stochastic";
import { createADX } from "@/lib/chart-helpers/indicators-KLine/adx";
import { createATR } from "@/lib/chart-helpers/indicators-KLine/atr";
import { createEnvelopes } from "@/lib/chart-helpers/indicators-KLine/envelopes";
import { createIchimoku } from "@/lib/chart-helpers/indicators-KLine/ichimoku";
import { createParabolicSAR } from "@/lib/chart-helpers/indicators-KLine/parabolicSAR";
import { createStandardDeviation } from "@/lib/chart-helpers/indicators-KLine/standardDeviation";
import { createCCI } from "@/lib/chart-helpers/indicators-KLine/cci";
import { createWilliamsR } from "@/lib/chart-helpers/indicators-KLine/williamsR";
import { createOBV } from "@/lib/chart-helpers/indicators-KLine/obv";
import { createMFI } from "@/lib/chart-helpers/indicators-KLine/mfi";
import { createMomentum } from "@/lib/chart-helpers/indicators-KLine/momentum";
import { createAwesomeOscillator } from "@/lib/chart-helpers/indicators-KLine/awesomeOscillator";
import { createBearsPower } from "@/lib/chart-helpers/indicators-KLine/bearsPower";
import { createBullsPower } from "@/lib/chart-helpers/indicators-KLine/bullsPower";
import { createDeMarker } from "@/lib/chart-helpers/indicators-KLine/deMarker";
import { createForceIndex } from "@/lib/chart-helpers/indicators-KLine/forceIndex";
import { createOSMA } from "@/lib/chart-helpers/indicators-KLine/osma";
import { createRVI } from "@/lib/chart-helpers/indicators-KLine/rvi";
import { createAD } from "@/lib/chart-helpers/indicators-KLine/ad";
import { createVolumes } from "@/lib/chart-helpers/indicators-KLine/volumes";
import { createAcceleratorOscillator } from "@/lib/chart-helpers/indicators-KLine/acceleratorOscillator";
import { createAlligator } from "@/lib/chart-helpers/indicators-KLine/alligator";
import { createFractals } from "@/lib/chart-helpers/indicators-KLine/fractals";
import { createGatorOscillator } from "@/lib/chart-helpers/indicators-KLine/gatorOscillator";
import { createMarketFacilitationIndex } from "@/lib/chart-helpers/indicators-KLine/marketFacilitationIndex";

export type LineStyle = "solid" | "dashed" | "dotted";

export interface IndicatorStyleSetting {
  color: string;
  style: LineStyle;
}

export interface IndicatorInstanceSettings {
  values: Record<string, number | string>;
  styles: Record<string, IndicatorStyleSetting>;
  /** @deprecated migrated to styles on read */
  styleSettings?: Record<string, IndicatorStyleSetting>;
}

export interface IndicatorInstance {
  id: string;
  indicatorId: string;
  name: string;
  createsNewChart: boolean;
  category: string;
  parameters?: CatalogEntry["parameters"];
  settings?: IndicatorInstanceSettings;
  visible?: boolean;
}

export interface IndicatorStyleSchema {
  id: string;
  label: string;
  defaultColor: string;
  defaultStyle: LineStyle;
}

export type KLineIndicatorTemplate = ReturnType<typeof createAdvancedMA>;

type IndicatorBuilder = (settings: IndicatorInstanceSettings | undefined) => KLineIndicatorTemplate;

export interface IndicatorDefinition extends CatalogEntry {
  pane: "overlay" | "separate";
  enabled: boolean;
  styles: IndicatorStyleSchema[];
  build: IndicatorBuilder;
}

/** Indicators exposed in the Add list */
export const ENABLED_INDICATOR_IDS = new Set([
  "ma",
  "bollinger-bands",
  "adx",
  "envelopes",
  "ichimoku",
  "parabolic-sar",
  "std-dev",
  "rsi",
  "macd",
  "stochastic",
  "atr",
  "cci",
  "wpr",
  "momentum",
  "bears-power",
  "bulls-power",
  "demarker",
  "force-index",
  "osma",
  "rvi",
  "obv",
  "mfi",
  "ad",
  "volumes",
  "ao",
  "ac",
  "alligator",
  "gator",
  "fractals",
  "mfi-bw",
]);

const STYLE_PRESETS: Record<string, IndicatorStyleSchema[]> = {
  ma: [
    { id: "ma", label: "MA", defaultColor: "#FF9600", defaultStyle: "solid" },
  ],
  "bollinger-bands": [
    { id: "upper", label: "Upper", defaultColor: "#FF9600", defaultStyle: "solid" },
    { id: "middle", label: "Middle", defaultColor: "#2196F3", defaultStyle: "solid" },
    { id: "lower", label: "Lower", defaultColor: "#FF9600", defaultStyle: "solid" },
  ],
  adx: [
    { id: "adx", label: "ADX", defaultColor: "#FFA500", defaultStyle: "solid" },
    { id: "diplus", label: "+DI", defaultColor: "#00FF00", defaultStyle: "solid" },
    { id: "diminus", label: "-DI", defaultColor: "#FF0000", defaultStyle: "solid" },
  ],
  envelopes: [
    { id: "upper", label: "Upper", defaultColor: "#2196F3", defaultStyle: "solid" },
    { id: "middle", label: "Middle", defaultColor: "#FF9600", defaultStyle: "solid" },
    { id: "lower", label: "Lower", defaultColor: "#2196F3", defaultStyle: "solid" },
  ],
  ichimoku: [
    { id: "tenkan", label: "Tenkan-sen", defaultColor: "#0496FF", defaultStyle: "solid" },
    { id: "kijun", label: "Kijun-sen", defaultColor: "#EF476F", defaultStyle: "solid" },
    { id: "chikou", label: "Chikou Span", defaultColor: "#26A69A", defaultStyle: "solid" },
    { id: "spanA", label: "Senkou Span A", defaultColor: "#26A69A", defaultStyle: "solid" },
    { id: "spanB", label: "Senkou Span B", defaultColor: "#EF5350", defaultStyle: "solid" },
  ],
  "parabolic-sar": [
    { id: "sar", label: "SAR", defaultColor: "#FF9600", defaultStyle: "solid" },
  ],
  "std-dev": [
    { id: "std", label: "Std Dev", defaultColor: "#2196F3", defaultStyle: "solid" },
  ],
  rsi: [
    { id: "rsi", label: "RSI", defaultColor: "#B8860B", defaultStyle: "solid" },
  ],
  macd: [
    { id: "macd", label: "MACD", defaultColor: "#2196F3", defaultStyle: "solid" },
    { id: "signal", label: "Signal", defaultColor: "#FF9600", defaultStyle: "dashed" },
    { id: "histogramUp", label: "Histogram Up", defaultColor: "#26A69A", defaultStyle: "solid" },
    { id: "histogramDown", label: "Histogram Down", defaultColor: "#EF5350", defaultStyle: "solid" },
  ],
  stochastic: [
    { id: "k", label: "%K", defaultColor: "#00FF00", defaultStyle: "solid" },
    { id: "d", label: "%D", defaultColor: "#FF0000", defaultStyle: "dashed" },
  ],
  atr: [
    { id: "atr", label: "ATR", defaultColor: "#2196F3", defaultStyle: "solid" },
  ],
  cci: [
    { id: "cci", label: "CCI", defaultColor: "#2196F3", defaultStyle: "solid" },
  ],
  wpr: [
    { id: "wpr", label: "Williams %R", defaultColor: "#2196F3", defaultStyle: "solid" },
  ],
  momentum: [
    { id: "momentum", label: "Momentum", defaultColor: "#2196F3", defaultStyle: "solid" },
  ],
  "bears-power": [
    { id: "value", label: "Bears Power", defaultColor: "#EF5350", defaultStyle: "solid" },
  ],
  "bulls-power": [
    { id: "value", label: "Bulls Power", defaultColor: "#26A69A", defaultStyle: "solid" },
  ],
  demarker: [
    { id: "dem", label: "DeMarker", defaultColor: "#2196F3", defaultStyle: "solid" },
  ],
  "force-index": [
    { id: "fi", label: "Force Index", defaultColor: "#2196F3", defaultStyle: "solid" },
  ],
  osma: [
    { id: "osmaUp", label: "OSMA Up", defaultColor: "#26A69A", defaultStyle: "solid" },
    { id: "osmaDown", label: "OSMA Down", defaultColor: "#EF5350", defaultStyle: "solid" },
  ],
  rvi: [
    { id: "rvi", label: "RVI", defaultColor: "#2196F3", defaultStyle: "solid" },
    { id: "signal", label: "Signal", defaultColor: "#FF9600", defaultStyle: "dashed" },
  ],
  obv: [
    { id: "obv", label: "OBV", defaultColor: "#2196F3", defaultStyle: "solid" },
  ],
  mfi: [
    { id: "mfi", label: "MFI", defaultColor: "#2196F3", defaultStyle: "solid" },
  ],
  ad: [
    { id: "ad", label: "A/D", defaultColor: "#2196F3", defaultStyle: "solid" },
  ],
  volumes: [
    { id: "volumeUp", label: "Volume Up", defaultColor: "#26A69A", defaultStyle: "solid" },
    { id: "volumeDown", label: "Volume Down", defaultColor: "#EF5350", defaultStyle: "solid" },
  ],
  ao: [
    { id: "aoUp", label: "AO Up", defaultColor: "#26A69A", defaultStyle: "solid" },
    { id: "aoDown", label: "AO Down", defaultColor: "#EF5350", defaultStyle: "solid" },
  ],
  ac: [
    { id: "acUp", label: "AC Up", defaultColor: "#26A69A", defaultStyle: "solid" },
    { id: "acDown", label: "AC Down", defaultColor: "#EF5350", defaultStyle: "solid" },
  ],
  alligator: [
    { id: "jaw", label: "Jaw", defaultColor: "#2196F3", defaultStyle: "solid" },
    { id: "teeth", label: "Teeth", defaultColor: "#EF5350", defaultStyle: "solid" },
    { id: "lips", label: "Lips", defaultColor: "#26A69A", defaultStyle: "solid" },
  ],
  gator: [
    { id: "upperUp", label: "Upper Up", defaultColor: "#26A69A", defaultStyle: "solid" },
    { id: "upperDown", label: "Upper Down", defaultColor: "#EF5350", defaultStyle: "solid" },
    { id: "lowerUp", label: "Lower Up", defaultColor: "#26A69A", defaultStyle: "solid" },
    { id: "lowerDown", label: "Lower Down", defaultColor: "#EF5350", defaultStyle: "solid" },
  ],
  fractals: [
    { id: "upFractal", label: "Up Fractal", defaultColor: "#26A69A", defaultStyle: "solid" },
    { id: "downFractal", label: "Down Fractal", defaultColor: "#EF5350", defaultStyle: "solid" },
  ],
  "mfi-bw": [
    { id: "green", label: "Green", defaultColor: "#26A69A", defaultStyle: "solid" },
    { id: "fade", label: "Fade", defaultColor: "#9C27B0", defaultStyle: "solid" },
    { id: "fake", label: "Fake", defaultColor: "#795548", defaultStyle: "solid" },
    { id: "squat", label: "Squat", defaultColor: "#2196F3", defaultStyle: "solid" },
  ],
};

const BUILDERS: Record<string, IndicatorBuilder> = {
  ma: (s) => createAdvancedMA(normalizeSettingsForEngine(s)),
  "bollinger-bands": (s) => createBollingerBands(normalizeSettingsForEngine(s)),
  rsi: (s) => createRSI(normalizeSettingsForEngine(s)),
  macd: (s) => createMACD(normalizeSettingsForEngine(s)),
  stochastic: (s) => createStochastic(normalizeSettingsForEngine(s)),
  adx: (s) => createADX(normalizeSettingsForEngine(s)),
  atr: (s) => createATR(normalizeSettingsForEngine(s)),
  envelopes: (s) => createEnvelopes(normalizeSettingsForEngine(s)),
  ichimoku: (s) => createIchimoku(normalizeSettingsForEngine(s)),
  "parabolic-sar": (s) => createParabolicSAR(normalizeSettingsForEngine(s)),
  "std-dev": (s) => createStandardDeviation(normalizeSettingsForEngine(s)),
  cci: (s) => createCCI(normalizeSettingsForEngine(s)),
  wpr: (s) => createWilliamsR(normalizeSettingsForEngine(s)),
  obv: (s) => createOBV(normalizeSettingsForEngine(s)),
  mfi: (s) => createMFI(normalizeSettingsForEngine(s)),
  momentum: (s) => createMomentum(normalizeSettingsForEngine(s)),
  ao: (s) => createAwesomeOscillator(normalizeSettingsForEngine(s)),
  "awesome-oscillator": (s) => createAwesomeOscillator(normalizeSettingsForEngine(s)),
  "bears-power": (s) => createBearsPower(normalizeSettingsForEngine(s)),
  "bulls-power": (s) => createBullsPower(normalizeSettingsForEngine(s)),
  demarker: (s) => createDeMarker(normalizeSettingsForEngine(s)),
  "force-index": (s) => createForceIndex(normalizeSettingsForEngine(s)),
  osma: (s) => createOSMA(normalizeSettingsForEngine(s)),
  rvi: (s) => createRVI(normalizeSettingsForEngine(s)),
  ad: (s) => createAD(normalizeSettingsForEngine(s)),
  volumes: (s) => createVolumes(normalizeSettingsForEngine(s)),
  ac: (s) => createAcceleratorOscillator(normalizeSettingsForEngine(s)),
  "accelerator-oscillator": (s) =>
    createAcceleratorOscillator(normalizeSettingsForEngine(s)),
  alligator: (s) => createAlligator(normalizeSettingsForEngine(s)),
  fractals: (s) => createFractals(normalizeSettingsForEngine(s)),
  gator: (s) => createGatorOscillator(normalizeSettingsForEngine(s)),
  "gator-oscillator": (s) => createGatorOscillator(normalizeSettingsForEngine(s)),
  "mfi-bw": (s) => createMarketFacilitationIndex(normalizeSettingsForEngine(s)),
  "market-facilitation-index": (s) =>
    createMarketFacilitationIndex(normalizeSettingsForEngine(s)),
};

function normalizeSettingsForEngine(
  settings: IndicatorInstanceSettings | undefined
): IndicatorInstanceSettings {
  if (!settings) return { values: {}, styles: {} };
  const styles =
    settings.styles ??
    settings.styleSettings ??
    {};
  return {
    values: settings.values ?? {},
    styles,
  };
}

function buildDefinition(entry: CatalogEntry): IndicatorDefinition {
  const build = BUILDERS[entry.indicatorId];
  if (!build) {
    throw new Error(`No KLine builder for indicator: ${entry.indicatorId}`);
  }
  return {
    ...entry,
    pane: entry.createsNewChart ? "separate" : "overlay",
    enabled: ENABLED_INDICATOR_IDS.has(entry.indicatorId),
    styles: STYLE_PRESETS[entry.indicatorId] ?? [],
    build,
  };
}

export const indicatorRegistry: Record<string, IndicatorDefinition> =
  Object.fromEntries(
    indicatorCatalog.map((entry) => [entry.indicatorId, buildDefinition(entry)])
  );

export function getIndicatorDefinition(
  indicatorId: string
): IndicatorDefinition | undefined {
  return indicatorRegistry[indicatorId];
}

export function buildIndicatorTemplate(
  indicatorId: string,
  settings?: IndicatorInstanceSettings
): KLineIndicatorTemplate {
  const def = getIndicatorDefinition(indicatorId);
  if (!def) {
    throw new Error(`Unknown indicator type: ${indicatorId}`);
  }
  return def.build(settings);
}

/** Stable KLineCharts registration name per instance */
export function getInstanceChartName(
  indicatorId: string,
  instanceId: string
): string {
  return `vybe_${indicatorId}_${instanceId}`;
}

export function getDefaultSettings(
  indicatorId: string
): IndicatorInstanceSettings {
  const def = getIndicatorDefinition(indicatorId);
  if (!def) {
    return { values: {}, styles: {} };
  }
  const values: Record<string, number | string> = {};
  def.parameters.forEach((p) => {
    values[p.name] = p.defaultValue;
  });
  const styles: Record<string, IndicatorStyleSetting> = {};
  def.styles.forEach((s) => {
    styles[s.id] = { color: s.defaultColor, style: s.defaultStyle };
  });
  return { values, styles };
}

export function formatParamSummary(instance: IndicatorInstance): string {
  const values = instance.settings?.values;
  if (!values) return "";
  const parts: string[] = [];
  if (values.Period != null) parts.push(String(values.Period));
  if (values.Method != null) parts.push(String(values.Method));
  if (values["Apply to"] != null) parts.push(String(values["Apply to"]));
  if (values.Deviations != null) parts.push(`±${values.Deviations}`);
  return parts.slice(0, 3).join(" · ");
}

export interface IndicatorCategoryView {
  id: IndicatorCategoryId;
  name: string;
  indicators: Array<
    CatalogEntry & { enabled: boolean; pane: "overlay" | "separate" }
  >;
}

/** Categories for the Add-indicator UI (enabled only) */
export function getEnabledIndicatorCategories(): IndicatorCategoryView[] {
  const byCategory = new Map<IndicatorCategoryId, IndicatorCategoryView>();

  Object.values(indicatorRegistry)
    .filter((d) => d.enabled)
    .forEach((def) => {
      if (!byCategory.has(def.category)) {
        byCategory.set(def.category, {
          id: def.category,
          name:
            def.category === "trend"
              ? "TREND"
              : def.category === "oscillators"
                ? "OSCILLATORS"
                : def.category === "volumes"
                  ? "VOLUMES"
                  : def.category === "bill-williams"
                    ? "BILL WILLIAMS"
                    : "CUSTOM",
          indicators: [],
        });
      }
      byCategory.get(def.category)!.indicators.push({
        indicatorId: def.indicatorId,
        name: def.name,
        category: def.category,
        createsNewChart: def.createsNewChart,
        parameters: def.parameters,
        enabled: def.enabled,
        pane: def.pane,
      });
    });

  const order: IndicatorCategoryId[] = [
    "trend",
    "oscillators",
    "volumes",
    "bill-williams",
    "custom",
  ];
  return order
    .map((id) => byCategory.get(id))
    .filter((c): c is IndicatorCategoryView => c != null && c.indicators.length > 0);
}

/** Legacy shape for components still importing indicatorCategories */
export const indicatorCategories = getEnabledIndicatorCategories();

export function catalogEntryToInstance(
  indicatorId: string,
  partial?: Partial<IndicatorInstance>
): IndicatorInstance {
  const catalog = indicatorCatalogById[indicatorId];
  const def = getIndicatorDefinition(indicatorId);
  if (!catalog || !def) {
    throw new Error(`Unknown indicator: ${indicatorId}`);
  }
  return {
    indicatorId,
    name: catalog.name,
    createsNewChart: catalog.createsNewChart,
    category: catalog.category,
    parameters: catalog.parameters,
    settings: partial?.settings ?? getDefaultSettings(indicatorId),
    visible: partial?.visible ?? true,
    id:
      partial?.id ??
      `id_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
    ...partial,
  };
}
